import { ShoppingCart, CheckCircle2, Circle, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import type { BootStepState } from '@/services/boot.service';

interface BootScreenProps {
  steps: BootStepState[];
  progress: number;
  error: string | null;
  onRetry: () => void;
}

export function BootScreen({ steps, progress, error, onRetry }: BootScreenProps) {
  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center select-none"
      style={{
        background: 'linear-gradient(180deg, #1a1f2e 0%, #151922 100%)',
      }}
    >
      {/* ── Logo ── */}
      <div className="mb-8">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-blue-500/20">
          <ShoppingCart size={36} className="text-white" />
        </div>
        <h1 className="text-white text-2xl font-bold text-center tracking-tight">
          POS Empresarial
        </h1>
        <p className="text-white/35 text-sm text-center mt-1.5 font-medium">
          Sistema de Punto de Venta
        </p>
      </div>

      {/* ── Progress Bar ── */}
      <div className="w-72 mb-8">
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${Math.round(progress)}%` }}
          />
        </div>
        <p className="text-white/25 text-xs text-center mt-2 font-mono tracking-wide">
          {Math.round(progress)}%
        </p>
      </div>

      {/* ── Steps ── */}
      <div className="space-y-3 w-72">
        {steps.map((step) => {
          const isActive = step.status === 'running';
          const isDone = step.status === 'done';
          const isError = step.status === 'error';
          const isPending = step.status === 'pending';

          return (
            <div key={step.id} className="flex items-center gap-3 min-h-[28px]">
              {/* Icon */}
              {isDone && (
                <CheckCircle2 size={18} className="text-green-400 flex-shrink-0 transition-all duration-300" />
              )}
              {isActive && (
                <Loader2 size={18} className="text-blue-400 animate-spin flex-shrink-0" />
              )}
              {isError && (
                <AlertCircle size={18} className="text-red-400 flex-shrink-0" />
              )}
              {isPending && (
                <Circle size={18} className="text-white/15 flex-shrink-0" />
              )}

              {/* Label */}
              <span
                className={`text-sm transition-all duration-300 ${
                  isDone
                    ? 'text-white/60'
                    : isActive
                      ? 'text-white font-medium'
                      : isError
                        ? 'text-red-400'
                        : 'text-white/25'
                }`}
              >
                {step.description}
                {isActive && (
                  <span className="inline-flex">
                    <span className="animate-pulse">.</span>
                    <span className="animate-pulse" style={{ animationDelay: '0.2s' }}>.</span>
                    <span className="animate-pulse" style={{ animationDelay: '0.4s' }}>.</span>
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── Error + Retry ── */}
      {error && (
        <div className="mt-8 flex flex-col items-center gap-4">
          <div className="bg-red-500/10 border border-red-500/15 rounded-xl px-5 py-3 max-w-xs text-center">
            <p className="text-red-400 text-xs leading-relaxed">{error}</p>
          </div>
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/15 active:bg-white/20 transition-all active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-white/20"
          >
            <RefreshCw size={15} />
            Reintentar
          </button>
        </div>
      )}

      {/* ── Version ── */}
      <p className="absolute bottom-7 text-white/15 text-xs font-mono">
        POS Empresarial v0.1.0
      </p>
    </div>
  );
}
